import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { products, orders } from '@/utils/api';
import { toast } from 'sonner';
import useRazorpay from 'react-razorpay';

const Checkout = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [Razorpay] = useRazorpay();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [shippingAddress, setShippingAddress] = useState('');
  const user = JSON.parse(localStorage.getItem('user') || 'null');

  useEffect(() => {
    if (!user || user.userType !== 'buyer') {
      toast.error('Please login as a buyer to purchase');
      navigate('/login');
      return;
    }
    loadProduct();
  }, [productId]);

  const loadProduct = async () => {
    try {
      const response = await products.getById(productId);
      setProduct(response.data);
    } catch (error) {
      toast.error('Failed to load product');
      navigate('/products');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    if (!shippingAddress.trim()) {
      toast.error('Please enter shipping address');
      return;
    }

    setProcessing(true);

    try {
      // Create order
      const orderResponse = await orders.create({
        productId: product.id,
        quantity: 1,
        shippingAddress
      });

      const { order, razorpayOrderId, razorpayKeyId } = orderResponse.data;

      // Initialize Razorpay
      const options = {
        key: razorpayKeyId,
        amount: order.totalAmount * 100,
        currency: 'INR',
        name: 'ARTCARDO',
        description: product.title,
        order_id: razorpayOrderId,
        handler: async (response) => {
          try {
            // Verify payment
            await orders.verifyPayment({
              orderId: order.id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpayOrderId: response.razorpay_order_id,
              razorpaySignature: response.razorpay_signature
            });

            toast.success('Payment successful! Order confirmed.');
            navigate('/buyer-dashboard');
          } catch (error) {
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: user.name,
          email: user.email,
          contact: user.phone || ''
        },
        theme: {
          color: '#ef4444'
        }
      };

      const razorpayInstance = new Razorpay(options);
      razorpayInstance.open();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create order');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="checkout-page">
        <Navbar />
        <div className="section" style={{paddingTop: "180px"}}>
          <div className="text-center py-12">Loading...</div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!product) return null;

  const gstAmount = product.price * (product.gstRate / 100);
  const totalPrice = product.price + gstAmount;

  return (
    <div data-testid="checkout-page">
      <Navbar />
      
      <div style={{paddingTop: "180px"}}>
        <section className="section">
          <h1 className="section-title" data-testid="checkout-title">Checkout</h1>

          <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8">
            <div className="card" data-testid="order-summary">
              <h2 className="text-2xl font-semibold mb-4">Order Summary</h2>
              <div className="flex gap-4 mb-6">
                <img
                  src={products.getImage(product.images[0])}
                  alt={product.title}
                  className="w-24 h-24 object-cover rounded"
                />
                <div>
                  <h3 className="font-semibold">{product.title}</h3>
                  <p className="text-sm text-gray-600">by {product.artistName}</p>
                  <p className="text-sm text-gray-500">Quantity: 1</p>
                </div>
              </div>

              <div className="space-y-2 border-t pt-4">
                <div className="flex justify-between">
                  <span>Base Price:</span>
                  <span>₹{product.price.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span>GST ({product.gstRate}%):</span>
                  <span>₹{gstAmount.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-lg font-bold border-t pt-2">
                  <span>Total:</span>
                  <span className="text-[#ef4444]">₹{totalPrice.toLocaleString()}</span>
                </div>
              </div>
            </div>

            <div className="card" data-testid="shipping-form">
              <h2 className="text-2xl font-semibold mb-4">Shipping Details</h2>
              
              <div className="form-group">
                <label className="form-label">Name</label>
                <Input value={user.name} disabled className="form-input" />
              </div>

              <div className="form-group">
                <label className="form-label">Email</label>
                <Input value={user.email} disabled className="form-input" />
              </div>

              <div className="form-group">
                <label className="form-label">Phone</label>
                <Input value={user.phone || ''} disabled className="form-input" />
              </div>

              <div className="form-group">
                <label className="form-label">Shipping Address *</label>
                <Textarea
                  value={shippingAddress}
                  onChange={(e) => setShippingAddress(e.target.value)}
                  placeholder="Enter your complete shipping address"
                  className="form-textarea"
                  rows={4}
                  required
                  data-testid="shipping-address-input"
                />
              </div>

              <Button
                onClick={handlePayment}
                disabled={processing || !shippingAddress.trim()}
                className="w-full py-6 text-lg"
                style={{background: '#ef4444'}}
                data-testid="proceed-payment-btn"
              >
                {processing ? 'Processing...' : `Pay ₹${totalPrice.toLocaleString()}`}
              </Button>

              <p className="text-xs text-gray-500 text-center mt-4">
                Powered by Razorpay - Secure Payment Gateway
              </p>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default Checkout;